import { createContext, useContext, useState, ReactNode } from 'react'
import { api } from '@/services/api'

interface EasterEggContextType {
  isEasterEggActive: boolean
  activateEasterEgg: (method?: string) => void
  deactivateEasterEgg: () => void
}

const EasterEggContext = createContext<EasterEggContextType | undefined>(undefined)

// The special character list for filtering
export const EASTER_EGG_CHARACTERS = [
  "Nami",
  "Nico Robin",
  "Nefertari Vivi",
  "Yamato",
  "Boa Hancock",
  "Boa Sandersonia",
  "Boa Marigold",
  "Margaret",
  "Aphelandra",
  "Sweet Pea",
  "Ran",
  "Daisy",
  "Cosmos",
  "Rindo",
  "Gloriosa",
  "Tashigi",
  "Hina",
  "Tsuru",
  "Stussy",
  "Rebecca",
  "Viola",
  "Scarlet",
  "Shirahoshi",
  "Otohime",
  "Camie",
  "Madame Shyarly",
  "Charlotte Smoothie",
  "Charlotte Compote",
  "Charlotte Galette",
  "Charlotte Brûlée",
  "Charlotte Amande",
  "Charlotte Custard",
  "Charlotte Citron",
  "Charlotte Cinnamon",
  "Charlotte Myukuru",
  "Charlotte Brownie",
  "Charlotte Pudding",
  "Charlotte Flampe",
  "Charlotte Praline",
  "Kozuki Hiyori",
  "Komurasaki",
  "Kikunojo",
  "Speed",
  "Black Maria",
  "Ulti",
  "Jewelry Bonney",
  "Alvida",
  "Catarina Devon",
  "Miss Doublefinger",
  "Zala",
  "Miss Valentine",
  "Mikita",
  "Perona",
  "Kalifa",
  "Monet",
  "Baby 5"
]

export function EasterEggProvider({ children }: { children: ReactNode }) {
  const [isEasterEggActive, setIsEasterEggActive] = useState(false)

  const activateEasterEgg = async (method: string = 'unknown') => {
    // Toggle the easter egg state
    const newState = !isEasterEggActive
    setIsEasterEggActive(newState)
    
    // Log activation to backend (only when activating, not deactivating)
    if (newState) {
      try {
        await api.post('/api/activity/easter-egg', {
          action: 'boobies_mode_activated',
          method: method // 'keyboard' or 'tap'
        })
      } catch (error) {
        // Don't block the easter egg if logging fails
        console.log('Easter egg activated but could not log to server')
      }
    }
  }

  const deactivateEasterEgg = () => {
    setIsEasterEggActive(false)
  }

  return (
    <EasterEggContext.Provider value={{ isEasterEggActive, activateEasterEgg, deactivateEasterEgg }}>
      {children}
    </EasterEggContext.Provider>
  )
}

export function useEasterEgg() {
  const context = useContext(EasterEggContext)
  if (context === undefined) {
    throw new Error('useEasterEgg must be used within an EasterEggProvider')
  }
  return context
}

// Helper function to check if a card matches the easter egg filter
export function isEasterEggCard(cardName: string): boolean {
  const lowerCardName = cardName.toLowerCase()
  return EASTER_EGG_CHARACTERS.some(character => 
    lowerCardName.includes(character.toLowerCase())
  )
}